import uniqBy from "lodash/uniqBy";
import debounce from "lodash/debounce";
import React, { useCallback, useMemo, useRef, useState } from "react";

import { queryPicklistAction } from "../../../../Engine/ActionCreators/PicklistActionCreators";
import { getFilterKey } from "../../../../Helpers/FilteringHelpers";
import { FilterSourceInfoType, getFilterBySourceId, MultiLineFilter } from "../../MultiLine/MultiLineFilter";
import { AbsoluteModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { PathResolver, withResolvePaths } from "../../../withResolvePaths";
import { useTypedDispatch } from "../../../../../../Common/UseTypedSelector";
import { useMultilineClient } from "../../../../Engine/Core/Filtering/UseMultilineClient";
import { createHocDisplayName } from "../../../TypingUtils";
import { ICandyApiDraftClient } from "../../../../../../Common/CandyApiClient/ICandyApiClient";
import { InnerQueryRequest } from "../../../../../../Common/CandyApiClient/Types/QueryCollecton/InnerQueryRequest";
import { SelectClause } from "../../../../../../Common/CandyApiClient/Types/QueryCollecton/SelectClause";
import { ValueReferenceExpression } from "../../../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/ValueReferenceExpression";
import { getMatchedAndDifferentModelPaths } from "../../../../../../Common/ModelPath/PathSplitHelper";
import {
    arrayHasAtLeastOneItem,
    isNotNullOrEmpty,
    isNotNullOrUndefined,
    reject,
} from "../../../../../../Common/TypingUtils";
import { StringMatchExpression } from "../../../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/StringMatchExpression";
import { OrExpression } from "../../../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/OrExpression";
import { withCandyApiDraftClient } from "../../../withCandyApiDraftClient";
import {
    FilterAndExpression,
    FilterEqualExpression,
    FilterExpression,
    OperatorType,
} from "../../../../Helpers/FilterExpressions/FilterExpression";
import { IMultilineManagerClient } from "../../../../Engine/Core/Filtering/MultilineManager";
import {
    DigestFilter,
    DigestFilterOperator,
    DigestLogicalOperator,
} from "../../../../../../Common/CandyApiClient/DigestApiClient/Types/DigestQueryFilter";
import { CandyDispatch } from "../../../../Engine/Core/Store/CandyReduxTypes";
import { createAutoCancellableAsyncQueue } from "../../../../Engine/Core/Calculator2/Calculator2Compatibility";
import { useAsyncEffect } from "../../../../../../Common/UseAsyncEffect";
import { IterUtils } from "../../../../../../Common/IterableUtils";
import { NotExpression } from "../../../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/NotExpression";
import { AndExpression } from "../../../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/AndExpression";

import { FilterList, FilterListProps, Item } from "./FilterList";

const defaults = {
    separator: " ",
    fields: ["code", "name"] as [string, undefined | string],
    notFoundText: "Ничего не найдено",
    searchingForItemsCount: 10,
    maxTakeCount: 50,
};

interface FilterListOwnProps
    extends Omit<FilterListProps, "filteredUniqKey" | "separator" | "searchingForItemsCount" | "notFoundText"> {
    gId?: string;
    multilinePath: AbsoluteModelPath;
    dataPaths: AbsoluteModelPath[];
    resolvePath: PathResolver;
    filteredUniqKey?: string;
    separator: string | undefined;
    searchingForItemsCount: number | undefined;
    notFoundText: string | undefined;
    inputPlaceholder?: string;
    fields: [string, undefined | string] | undefined;
    candyApiDraftClient: ICandyApiDraftClient;
}

function splitSearchText(text: string, separator: string): string[] {
    const separators = [" ", "-", "—", "/", separator];
    const valuesToSearch = text
        .toLocaleLowerCase()
        .trim()
        .split(new RegExp(separators.join("|"), "g"))
        .filter(isNotNullOrEmpty);
    return valuesToSearch;
}

function getItemId(values: string[]) {
    return values.join("-");
}

function createLocalFilter(searchParts: string[]) {
    return (item: Item) =>
        searchParts.every((token: string, index: number) =>
            index === searchParts.length - 1
                ? Boolean(
                      item.values.some(v => v.toLocaleLowerCase().includes(token)) ||
                          (item.description && item.description.toLocaleLowerCase().includes(token))
                  )
                : Boolean(
                      item.values.some(v => v.toLocaleLowerCase() === token) ||
                          (item.description && item.description.toLocaleLowerCase() === token)
                  )
        );
}

async function getItemsFromServer(
    candyApi: ICandyApiDraftClient,
    multilinePath: AbsoluteModelPath,
    dataPaths: AbsoluteModelPath[],
    searchParts: string[],
    takeCount: number,
    dispatch: CandyDispatch,
    gId: string | undefined,
    fields: [string, undefined | string],
    abortSignal: AbortSignal
): Promise<Item[]> {
    const nestedMultiplicityPath = dataPaths[0]?.getPathWithoutLastIteration() ?? multilinePath;
    const fieldNames = dataPaths.map(x => {
        const restPart = getMatchedAndDifferentModelPaths(
            nestedMultiplicityPath,
            x
        ).differentPath.trimFirstStarIfFirstToken();
        return restPart.toString() + ".value";
    });
    const searchPartsForServer = gId == undefined ? searchParts : [];
    const filter = arrayHasAtLeastOneItem(searchPartsForServer)
        ? new OrExpression(
              searchPartsForServer
                  .map(searchPart =>
                      fieldNames.map(name => new StringMatchExpression(name, searchPart, "Substring", false))
                  )
                  .flat()
          )
        : undefined;
    const excludingEmptyItemsFilter = new OrExpression(
        fieldNames.map(name => new NotExpression(new StringMatchExpression(name, "", "Exact", false)))
    );
    const finalFilter =
        filter != undefined ? new AndExpression([filter, excludingEmptyItemsFilter]) : excludingEmptyItemsFilter;
    const itemsData = await candyApi.queryCollection(
        new InnerQueryRequest(
            nestedMultiplicityPath.toString(),
            fieldNames.map(name => new SelectClause(name, new ValueReferenceExpression(name))),
            finalFilter,
            undefined,
            fieldNames.map(name => new ValueReferenceExpression(name)),
            0,
            takeCount
        ),
        abortSignal
    );
    const serverItems = IterUtils.sortBy(
        Iterator.from(itemsData.data).map<Item>(x => {
            const values = x.map(field => (isNotNullOrUndefined(field) ? field : ""));
            return {
                id: getItemId(values),
                values: values,
            };
        }),
        item => item.id
    );

    const itemsWithDescriptions =
        gId != undefined ? await fillPicklistInfoAsync(dispatch, gId, serverItems, fields) : serverItems;

    const finalItems =
        gId != undefined && arrayHasAtLeastOneItem(searchParts)
            ? itemsWithDescriptions.filter(createLocalFilter(searchParts))
            : itemsWithDescriptions;

    return finalItems;
}

function applyFilter(
    client: IMultilineManagerClient,
    checkedItems: Item[],
    prevCheckedItems: Item[],
    resolvedDataPaths: AbsoluteModelPath[],
    filterSourceId: string
) {
    const prevCheckedIds = new Set(prevCheckedItems.map(x => x.id));
    const hasChanges =
        checkedItems.length === prevCheckedIds.size ? checkedItems.some(x => !prevCheckedIds.has(x.id)) : true;
    if (!hasChanges) {
        return;
    }

    const conditions = checkedItems.map(item => {
        const equalConditions: FilterEqualExpression[] = item.values.map(
            (val, index): FilterEqualExpression => ({
                operator: OperatorType.Equal,
                condition: {
                    dataPath: resolvedDataPaths[index]?.toLegacyPath() ?? reject(),
                    value: val,
                },
            })
        );
        const andExpression: FilterAndExpression = { operator: OperatorType.And, conditions: equalConditions };

        return andExpression;
    });

    const filterValue: FilterExpression | undefined = conditions.length
        ? { operator: OperatorType.Or, conditions: conditions }
        : undefined;

    const multilineFilter: MultiLineFilter | undefined =
        filterValue != undefined
            ? {
                  filterSourceId: filterSourceId,
                  expression: filterValue,
                  filterSourceInfo: {
                      type: FilterSourceInfoType.Items,
                      values: checkedItems.map(x => x.values),
                  },
              }
            : undefined;
    if (multilineFilter != undefined) {
        client.setFilter(multilineFilter);
    } else {
        client.removeFilter(filterSourceId);
    }
}

async function fillPicklistInfoAsync(
    dispatch: CandyDispatch,
    gId: string,
    items: Item[],
    fields: [string, undefined | string]
) {
    const valueField = fields[0];
    const descriptionField = fields != undefined ? fields[1] : undefined;
    const filterOperandsByItems = items
        .filter(x => x.description == undefined)
        .map(
            (item): DigestFilter => ({
                column: valueField,
                operator: DigestFilterOperator.Equal,
                value: item.values[0] ?? "",
            })
        );

    if (filterOperandsByItems.length === 0) {
        return items;
    }

    const filter = {
        filter: {
            operator: DigestLogicalOperator.Or,
            operands: filterOperandsByItems,
        },
    };

    const result = await dispatch(queryPicklistAction({ gId: gId }, filter));
    const digestItems = result.items;
    const newItems = items.map(item => {
        const digestItem = digestItems.find(i => valueField != undefined && item.values[0] === i[valueField]);

        if (digestItem && descriptionField && digestItem[descriptionField]) {
            return { ...item, description: digestItem[descriptionField]?.toString() };
        }

        return item;
    });
    return newItems;
}

const FilterListContainerWrapper: React.FC<FilterListOwnProps> = ownProps => {
    const { gId, multilinePath, dataPaths, resolvePath, filteredUniqKey = "default" } = ownProps;
    const dispatch = useTypedDispatch();
    const basePath = resolvePath(multilinePath);
    const { client, filters, useServerFilters } = useMultilineClient(basePath, filteredUniqKey, info => ({
        filters: info.multilineState.filters,
        client: info.client,
        useServerFilters: info.multilineState.useServerFilter,
    }));

    const { resolvedMultilinePath, resolvedDataPaths, currentFilterInfo, filterSourceId } = useMemo(() => {
        const innerResolvedDataPaths = dataPaths.map(path => resolvePath(path));
        const innerFilterSourceId = getFilterKey(innerResolvedDataPaths);
        const currentFilter = getFilterBySourceId(filters, innerFilterSourceId);
        const innerCurrentFilterInfo =
            currentFilter?.filterSourceInfo?.type === FilterSourceInfoType.Items
                ? currentFilter?.filterSourceInfo
                : undefined;
        return {
            resolvedMultilinePath: basePath,
            resolvedDataPaths: innerResolvedDataPaths,
            currentFilterInfo: innerCurrentFilterInfo,
            filterSourceId: innerFilterSourceId,
        };
    }, [filters, dataPaths]);

    const [items, setItems] = useState<Item[]>([]);
    const [searchString, setSearchString] = useState<string | undefined>();
    const [checkedItems, setCheckedItems] = useState<Item[]>(
        currentFilterInfo?.values?.map(x => ({
            id: x.join("-"),
            values: x,
        })) ?? []
    );
    const [restAvailableItems, setRestAvailableItems] = useState<Item[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const onSearchChange = useCallback((searchStringText: string) => {
        setSearchString(searchStringText);
    }, []);

    const prevCheckedItems = useRef(checkedItems);
    const applyFilterDebounced = useMemo(
        () =>
            debounce(
                (nextCheckedItems: Item[]) => {
                    applyFilter(client, nextCheckedItems, prevCheckedItems.current, resolvedDataPaths, filterSourceId);
                    prevCheckedItems.current = nextCheckedItems;
                },
                useServerFilters ? 1000 : 500
            ),
        [client, resolvedDataPaths, filterSourceId]
    );

    const updateSelectedItemsAndRestItems = useCallback(
        (nextCheckedItems: Item[], nextItems: Item[]) => {
            const checkedIds = new Set(nextCheckedItems.map(x => x.id));
            const filteredItems = Iterator.from(nextItems)
                .filter(item => !checkedIds.has(item.id))
                .toArray();

            setRestAvailableItems(filteredItems);
            setCheckedItems(nextCheckedItems);
            return nextCheckedItems;
        },
        [setCheckedItems, setRestAvailableItems]
    );

    const onSelectItem = useCallback(
        (itemId: string, checked: boolean) => {
            if (checked) {
                const checkedItem = items.find(item => item.id === itemId);
                if (checkedItem != undefined) {
                    const nextCheckedItems = updateSelectedItemsAndRestItems(
                        uniqBy([...checkedItems, checkedItem], x => x.id),
                        items
                    );
                    applyFilterDebounced(nextCheckedItems);
                }
            } else {
                const nextCheckedItems = updateSelectedItemsAndRestItems(
                    checkedItems.filter(item => item.id !== itemId),
                    items
                );
                applyFilterDebounced(nextCheckedItems);
            }
        },
        [items, checkedItems, applyFilterDebounced, updateSelectedItemsAndRestItems]
    );

    const onRemoveFilter = useCallback(() => {
        updateSelectedItemsAndRestItems([], items);
        applyFilterDebounced(checkedItems);
    }, [updateSelectedItemsAndRestItems, applyFilterDebounced]);

    const fields = ownProps.fields ?? defaults.fields;
    const workerQueue = useMemo(() => createAutoCancellableAsyncQueue(), []);

    useAsyncEffect(
        effectSignal => {
            workerQueue.enqueue(async queueSignal => {
                const linkedSignal = AbortSignal.any([queueSignal, effectSignal]);
                try {
                    if (linkedSignal.aborted) {
                        return;
                    }
                    setLoading(true);
                    await dispatch((_, __, form) => form.changeSet.send());
                    const searchParts = splitSearchText(searchString ?? "", ownProps.separator ?? defaults.separator);
                    const takeCount = defaults.maxTakeCount + checkedItems.length;

                    const nextItems = await getItemsFromServer(
                        ownProps.candyApiDraftClient,
                        resolvedMultilinePath,
                        resolvedDataPaths,
                        searchParts,
                        takeCount,
                        dispatch,
                        gId,
                        fields,
                        linkedSignal
                    );

                    const updatedCheckedItems =
                        gId != undefined
                            ? await fillPicklistInfoAsync(dispatch, gId, checkedItems, fields)
                            : checkedItems;

                    setItems(nextItems);
                    updateSelectedItemsAndRestItems(updatedCheckedItems, nextItems);
                } finally {
                    setLoading(false);
                }
            });
        },
        [
            dispatch,
            gId,
            fields,
            searchString,
            filterSourceId,
            useServerFilters,
            resolvedMultilinePath,
            updateSelectedItemsAndRestItems,
        ]
    );

    return (
        <FilterList
            {...ownProps}
            items={restAvailableItems}
            onRemoveFilter={onRemoveFilter}
            onSearchChange={onSearchChange}
            onSelectItem={onSelectItem}
            searching={isNotNullOrEmpty(searchString)}
            checkedItems={checkedItems}
            loading={loading}
            makeBaseValuesForMetrics={() => ({ path: ownProps.dataPaths.join("-") })}
            separator={ownProps.separator ?? defaults.separator}
            searchingForItemsCount={ownProps.searchingForItemsCount ?? defaults.searchingForItemsCount}
            notFoundText={ownProps.notFoundText ?? defaults.notFoundText}
        />
    );
};
FilterListContainerWrapper.displayName = createHocDisplayName(FilterList, "FilterListContainerWrapper");

export const FilterListContainer = withResolvePaths(withCandyApiDraftClient(FilterListContainerWrapper));
